import React, { useMemo, forwardRef, useImperativeHandle, useRef } from 'react';
import { Button, Message, Table } from '@bedrock/components';
import SInputPercent from '#components/SForm/SFormElement/InputPercent';
import { useMemoizedFn, useDebounceFn, useControllableValue } from 'ahooks';
import TableForm from '@mpc/common/src/components/TableForm';
import type { FormColumn } from '@mpc/common/src/components/TableForm';
import './index.less';

export interface DimensionWeight {
  dimension: string;
  /** 权重值，支持 number（API返回）或 string（表单使用） */
  weight: string | number;
}

export interface WeightSettingFormProps {
  value?: DimensionWeight[];
  onChange?: (value: DimensionWeight[]) => void;
  disabled?: boolean;
  readonly?: boolean;
  showValidation?: boolean;
}

export interface WeightSettingFormRef {
  validateFields: () => Promise<boolean>;
}

// 默认5个维度
const DEFAULT_DIMENSIONS = [
  '受众匹配度',
  '内容垂直度',
  '数据健康度',
  'CPM合理性',
  '风格契合度',
];


// 为空时默认
export const DEFAULT_DIMENSION_WEIGHTS = DEFAULT_DIMENSIONS.map(dimension => ({
  dimension,
  weight: (100 / DEFAULT_DIMENSIONS.length).toString(),
}));

// 将权重值转换为数字（支持 string 和 number 输入）
const coerceWeightToNumber = (value: unknown): number => {
  if (value === undefined || value === null || value === '') return 0;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return 0;
  // 这里不做 0-100 截断：避免输入值与校验/合计出现"误判不一致"
  return n;
};

// 创建默认权重值（默认为空字符串）
const createDefaultWeights = (): DimensionWeight[] => {
  return DEFAULT_DIMENSIONS.map((dimension) => ({
    dimension,
    weight: '',
  }));
};

const WeightSettingForm = forwardRef<WeightSettingFormRef, WeightSettingFormProps>(
  ({ disabled = false, showValidation = true, readonly = false, ...props }, ref) => {
    const tableRef = useRef<any>(null);

    // 使用可控值管理权重数据
    const [weights, setWeights] = useControllableValue<DimensionWeight[]>(props, {
      defaultValue: createDefaultWeights(),
    });

    // 确保有完整的5个维度
    const normalizedWeights = useMemo(() => {
      const next = weights && weights.length === 5 ? weights : createDefaultWeights();
      // 权重存储层保持 string；仅在 total/校验时转成数字
      return next.map((item) => ({
        ...item,
        weight:
          item.weight === undefined || item.weight === null || item.weight === ''
            ? '0'
            : String(item.weight),
      }));
    }, [weights]);

    // 计算合计
    const total = useMemo(() => {
      return normalizedWeights.reduce((sum, item) => {
        const w = coerceWeightToNumber(item.weight);
        return sum + w;
      }, 0);
    }, [normalizedWeights]);

    // 校验状态
    const isValid = total === 100;

    // 防抖处理权重变更
    const { run: debouncedOnChange } = useDebounceFn(
      (newWeights: DimensionWeight[]) => {
        setWeights(newWeights);
      },
      { wait: 300 }
    );

    // 处理单个权重变更
    const handleWeightChange = useMemoizedFn((dimension: string, weight: string | number | null) => {
      const num = coerceWeightToNumber(weight);
      const validWeight = String(num);

      const newWeights = normalizedWeights.map((item) =>
        item.dimension === dimension
          ? { ...item, weight: validWeight }
          : item
      );

      debouncedOnChange(newWeights);
    });

    // 均分权重
    const handleEqualDistribution = useMemoizedFn(() => {
      const newWeights = createDefaultWeights();
      setWeights(newWeights);
      Message.success('已均分权重');
    });

    // 校验提示
    const validationMessage = useMemo(() => {
      if (!showValidation) return null;
      if (isValid) return null;
      return `权重合计必须为100%，当前为 ${total}%`;
    }, [showValidation, isValid, total]);

    // 定义表格列 - 每个维度一列
    const columns: FormColumn[] = useMemo(() => {
      return DEFAULT_DIMENSIONS.map((dimension, index) => ({
        title: dimension,
        dataIndex: dimension,
        required: readonly ? false : true,
        width: 120,
        align: 'left' as const,
        formItem: {
          formItemKey: dimension,
          componentClass: 'inputPercent',
          view: !!readonly,
          elementProps: {
            valueType: "string",
            disabled,
            placeholder: '请输入权重',
          },
        },
      }));
    }, [disabled, handleWeightChange, readonly]);

    // 将权重数据转换为表格数据格式（一行，每列一个维度）
    const tableData = useMemo(() => {
      const row: Record<string, string> = {};
      normalizedWeights.forEach((item) => {
        row[item.dimension] = item.weight;
      });
      return [row];
    }, [normalizedWeights]);

    // 处理表格数据变更
    const handleTableChange = useMemoizedFn((newData: Record<string, unknown>[]) => {
      if (newData && newData.length > 0) {
        const row = newData[0];
        const newWeights = DEFAULT_DIMENSIONS.map((dimension) => ({
          dimension,
          weight:
            row?.[dimension] === undefined || row?.[dimension] === null || row?.[dimension] === ''
              ? ''
              : String(row?.[dimension]),
        }));
        setWeights(newWeights);
      }
    });

    // 暴露验证方法：表单项校验通过后，权重合计必须等于 100
    useImperativeHandle(
      ref,
      () => ({
        validateFields: async () => {
          try {
            if (tableRef.current?.validateFields) {
              let res = await tableRef.current.validateFields();
              if (!res) {
                return Promise.reject(res);
              }
            }
            const sum = normalizedWeights.reduce(
              (acc, item) => acc + coerceWeightToNumber(item.weight),
              0
            );
            const sumEquals100 = sum === 100;
            if (!sumEquals100) {
              return Promise.reject(`权重合计必须为100%，当前为 ${sum}%`);
            }
            
          } catch (error) {
            return Promise.reject(error);
          }
          return Promise.resolve(true);
        },
      }),
      [normalizedWeights]
    );

    return (
      <div className="weight-setting-form">
        {/* 表格区域 */}
        {!readonly && <div className="weight-setting-hint mb-2">
          权重支持输入0-100正整数，权重合计需等于100%
        </div>}
        <div className="weight-setting-table-wrapper">
          <TableForm
            ref={tableRef}
            value={tableData}
            onChange={handleTableChange}
            formColumns={columns}
            pagination={false}
            showHeader
            border
            summary={(_data) => {
              if (!!readonly) return null;
              return (
                <Table.Summary>
                  <Table.Summary.Row style={{ background: '#fff' }}>
                    <Table.Summary.Cell index={0} colSpan={DEFAULT_DIMENSIONS.length}>
                      <div className="weight-setting-summary">
                        <span className="weight-setting-summary-total">
                          权重合计:
                          <span className="total-number">{total}%</span>
                        </span>
                      </div>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
        </div>
      </div>
    );
  }
);

WeightSettingForm.displayName = 'WeightSettingForm';

export default WeightSettingForm;
